import React, { useContext, useState, useEffect, useRef } from 'react'
import '../styles/CreatePost.css'
import { AuthContext } from '../context/AuthContext'
import { ThemeContext } from '../context/ThemeContext'
import { formatModifiedTicker } from '../helper/StylesHelper'
import { formatCurrency as fc } from '../helper/StylesHelper'
import { fetchTransactionDetails } from '../helper/CreatePostHelper'
import { useNavigate } from 'react-router-dom'
import Skeleton from '@mui/material/Skeleton'
import {
  getChangeColor,
  getChangePercentageColor,
  getTypeOfQuantity,
  handleNavigateToAsset,
} from '../helper/MarketsHelper'
import 'react-datepicker/dist/react-datepicker.css'

export default function TransactionDetails({ transactionId }) {
  const navigate = useNavigate()
  const { userToken } = useContext(AuthContext)
  const { tc } = useContext(ThemeContext)

  const [transactionDetails, setTransactionDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isTransactionLoading, setIsTransactionLoading] = useState(false)
  const fetchTransactionRef = useRef(false)
  const lastTransactionIdRef = useRef(null)

  useEffect(() => {
    // Skip if already processing the same transactionId
    if (
      isTransactionLoading ||
      fetchTransactionRef.current ||
      !transactionId ||
      lastTransactionIdRef.current === transactionId
    ) {
      console.log('Skipping duplicate transaction fetch for ID:', transactionId)
      return
    }

    const fetchTransaction = async () => {
      console.log('Fetching transaction details for ID:', transactionId)

      // Set all guards immediately
      setLoading(true)
      setIsTransactionLoading(true)
      fetchTransactionRef.current = true
      lastTransactionIdRef.current = transactionId

      try {
        const transaction = await fetchTransactionDetails(
          transactionId,
          userToken
        )
        setTransactionDetails(transaction)
      } catch (error) {
        console.error('Error fetching transaction details:', error)
        // Reset lastTransactionIdRef on error so it can be retried
        lastTransactionIdRef.current = null
      } finally {
        setLoading(false)
        setIsTransactionLoading(false)
        fetchTransactionRef.current = false
      }
    }

    fetchTransaction()
  }, [transactionId, userToken])

  // Reset states when transactionId changes
  useEffect(() => {
    if (transactionId !== lastTransactionIdRef.current) {
      setTransactionDetails(null)
      setLoading(true)
      setIsTransactionLoading(false)
      fetchTransactionRef.current = false
      // Don't reset lastTransactionIdRef here as it will be set in the main useEffect
    }
  }, [transactionId])

  const renderPostTrade = (transactionDetails) => {
    if (!transactionDetails) {
      return null
    }

    const {
      type,
      ticker,
      currentPrice,
      name,
      username,
      date,
      quantity,
      assetType,
      copyCount,
      price,
    } = transactionDetails

    const formattedDate = new Date(date).toLocaleString()
    const isBuy = type === 'buy'
    const isOption = assetType === 'option'
    const isCrypto = assetType === 'crypto'
    const change = currentPrice - price
    const changePercent = (change / price) * 100

    if (!transactionDetails) {
      return <p>No transaction details available.</p>
    }

    return (
      <div
        className="PostTradeContainer"
        style={{
          backgroundColor: isBuy
            ? getChangeColor(changePercent, tc())
            : 'transparent',
          border:
            !isBuy &&
            `0.05rem solid ${getChangePercentageColor(-changePercent, tc())}`,
        }}
      >
        <div className="PostTradeHeader">
          <span className="PostTradeDate">
            <span className="bold">{name}</span> - {formattedDate}
          </span>
          <span className="PostTradeType bold">
            {isBuy ? 'BUY' : 'SELL'} - {formatModifiedTicker(ticker)}
          </span>
        </div>

        <div className="PostTradeContentContainer">
          <div className="PostTradeLabelAndValue">
            <span className="PostTradeDate">Price:</span>
            <span className="PostTradeType bold">${fc(price)}</span>
          </div>
          <div className="PostTradeLabelAndValue">
            <span className="PostTradeDate">Current Price:</span>
            <span className="PostTradeType bold">${fc(currentPrice)}</span>
          </div>
          <div className="PostTradeLabelAndValue">
            <span className="PostTradeDate">Price since:</span>
            <span
              className="PostTradeType bold"
              style={{
                color: getChangePercentageColor(changePercent, tc()),
              }}
            >
              {changePercent >= 0 && '+'}
              {fc(changePercent)}%
            </span>
          </div>
          <div className="PostTradeLabelAndValue">
            <span className="PostTradeDate">Copiers:</span>
            <span className="PostTradeType bold">{fc(copyCount, 0)}</span>
          </div>
          <div className="PostTradeLabelAndValue">
            <span className="PostTradeDate">{getTypeOfQuantity(ticker)}</span>
            <span className="PostTradeType bold">
              {fc(quantity, isCrypto ? 4 : 2)}
            </span>
          </div>
          <div className="PostTradeLabelAndValue">
            <span className="PostTradeDate">Market Value:</span>
            <span className="PostTradeType bold">
              ${fc(currentPrice * quantity * (isOption ? 100 : 1))}
            </span>
          </div>
        </div>

        <div className="PostTradeButtonsContainer">
          <button
            className="CreatePostFeedButton transBorder"
            onClick={() => {
              const isOption = assetType === 'option'
              const isCrypto = assetType === 'crypto'
              if (isOption) {
                navigate(`/options/${ticker}`, {
                  state: {
                    isCopy: true,
                    copyTransactionId: transactionId,
                    isBuy,
                  },
                })
              } else if (isCrypto) {
                navigate(`/crypto/${ticker}`, {
                  state: {
                    isCopy: true,
                    copyTransactionId: transactionId,
                    isBuy,
                  },
                })
              } else {
                navigate(`/stocks/${ticker}`, {
                  state: {
                    isCopy: true,
                    copyTransactionId: transactionId,
                    isBuy,
                  },
                })
              }
            }}
          >
            Copy trade
          </button>
          <button
            className="CreatePostFeedButton transBorder"
            onClick={() => {
              handleNavigateToAsset(navigate, ticker)
            }}
          >
            {formatModifiedTicker(ticker).split(' ')[0]}
          </button>
          {username && (
            <button
              className="CreatePostFeedButton transBorder"
              onClick={() => navigate(`/profile/${username}`)}
            >
              {name}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="CreatePostContainer">
      <div className="CreatePostContent">
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="90%"
            height={70}
            animation="wave"
            className="SkeletonLoader"
          />
        ) : (
          renderPostTrade(transactionDetails)
        )}
      </div>
    </div>
  )
}
